import type { JourneyStep, JourneyStepStatus } from "@/shared/components/JourneyProgressBar";

export type HomeState =
  | "setup-incomplete"
  | "no-authority"
  | "has-authority"
  | "someone-else-authority"
  | "settle-estate";

export type HomeStep = 1 | 2 | 3 | 4;

interface ResolveHomeStateInput {
  hasCompletedIntake: boolean;
  hasLegalAuthority: boolean;
  someoneElseHasAuthority: boolean;
  isAssetSearchComplete: boolean;
}

export interface HomeStateConfig {
  state: HomeState;
  step: HomeStep;
  journeySteps: JourneyStep[];
}

function makeJourneySteps(
  setup: JourneyStepStatus,
  getAuthorized: JourneyStepStatus,
  assetSearch: JourneyStepStatus,
  settleEstate: JourneyStepStatus,
): JourneyStep[] {
  return [
    { id: "setup", label: "Setup", status: setup },
    { id: "get-authorized", label: "Get authorized", status: getAuthorized },
    { id: "asset-search", label: "Find assets", status: assetSearch },
    { id: "settle-estate", label: "Settle the estate", status: settleEstate },
  ];
}

const STEP_1_STEPS = makeJourneySteps("current", "locked", "locked", "locked");
const STEP_2_STEPS = makeJourneySteps("complete", "current", "locked", "locked");
const STEP_3_STEPS = makeJourneySteps("complete", "complete", "current", "locked");
const STEP_4_STEPS = makeJourneySteps("complete", "complete", "complete", "current");

export function resolveHomeState(input: ResolveHomeStateInput): HomeStateConfig {
  if (!input.hasCompletedIntake) {
    return {
      state: "setup-incomplete",
      step: 1,
      journeySteps: STEP_1_STEPS,
    };
  }

  const base = {
    step: 2 as const,
    journeySteps: STEP_2_STEPS,
  };

  if (input.someoneElseHasAuthority && !input.hasLegalAuthority) {
    return { ...base, state: "someone-else-authority" };
  }

  if (!input.hasLegalAuthority) {
    return { ...base, state: "no-authority" };
  }

  if (input.isAssetSearchComplete) {
    return {
      state: "settle-estate",
      step: 4,
      journeySteps: STEP_4_STEPS,
    };
  }

  return {
    state: "has-authority",
    step: 3,
    journeySteps: STEP_3_STEPS,
  };
}
